// RouteErrorComponent — единая ошибка маршрута для ВСЕХ роутов.
//
// Ставится один раз как `defaultErrorComponent` роутера (`main.tsx`), а не по
// одному на каждый роут: своего `errorComponent` не было ни у одного из ~90
// роутов при 49 `pendingComponent`, то есть падение загрузчика (сеть, 500,
// битый параметр) доезжало до глобального boundary — а он подменяет собой всё
// приложение и нарисован inline-стилями, без темы и без i18n. Теперь ошибка
// остаётся внутри layout'а: шапка, навигация и остальной интерфейс на месте,
// сломан только контент маршрута.
//
// Границы ответственности:
//   • `LazyRouteBoundary` — ChunkLoadError ВНУТРИ уже отрисованного роута
//     (React.lazy подкомпонент), перезагружает документ при возврате сети;
//   • `LazyArt` — то же для ДЕКОРАТИВНОГО модуля (иллюстрация публичной
//     страницы): такой сбой вообще не должен доезжать сюда и уносить экран;
//   • `RouteErrorComponent` — ошибка загрузчика/рендера самого маршрута;
//   • `GlobalErrorBoundary` — всё, что вывалилось мимо обоих.
//
// Отправку в Hawk этот компонент НЕ делает — она живёт в `defaultOnCatch`
// роутера (`main.tsx`). Только там доступен `errorInfo.componentStack`, а без
// него разбор ошибок рендера («Maximum update depth exceeded» и подобных)
// упирается в минифицированный стек React. Здесь остаётся ровно экран.
// Экран «не загрузился модуль» переиспользуется из `LazyRouteBoundary`, чтобы
// тексты и кнопка не разъехались.

import { Button } from "@studyhub/ui/components/button";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { isChunkLoadError, recoverFromChunkLoad } from "@/lib/chunk-load";
import { userFacingErrorMessage } from "@/lib/user-facing-error";
import { ChunkLoadFallback } from "./lazy-route-boundary";

export function RouteErrorComponent({ error, reset }: ErrorComponentProps): React.ReactElement {
	const { t } = useTranslation("common");
	const router = useRouter();
	const online = useOnlineStatus();

	// Повтор = сбросить boundary И перезапросить загрузчики маршрута. Без
	// `invalidate()` boundary сбрасывается на тот же протухший результат
	// загрузчика и ошибка возвращается мгновенно.
	const retry = (): void => {
		reset();
		void router.invalidate();
	};

	// Мёртвый чанк не чинится ни сбросом boundary, ни перезапросом загрузчика:
	// отказ уже записан в module map документа. Единственный путь — новый
	// документ (`lib/chunk-load.ts`).
	if (isChunkLoadError(error)) {
		return <ChunkLoadFallback online={online} onRetry={recoverFromChunkLoad} />;
	}

	// Сырой `error.message` пользователю не показываем: офлайн он читается как
	// «Failed to fetch», а машинные коды не значат ничего. `userFacingErrorMessage`
	// пропускает только человеческий текст и переводит известные коды.
	const description = userFacingErrorMessage(error, t) ?? t("errors.boundary.body");

	return (
		<div className="flex min-h-[50dvh] items-center justify-center px-4">
			<ErrorState
				title={t("errors.boundary.title")}
				description={description}
				action={
					<Button variant="outline" className="cursor-pointer" onClick={retry}>
						{t("errors.boundary.retry")}
					</Button>
				}
			/>
		</div>
	);
}
